import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {patchJSONPromise, postJSONPromise} from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

const API_BASE = getApiBaseForObjectType(DomainGraphType.SCRATCH_PLATE);

// -- Shared nested types ---------------------------------------------------

export interface UnitInput {
  apiId: string;
  versionNumber?: number | null;
}

export interface QuantityInput {
  amount: number;
  unit?: UnitInput | null;
}

export interface ScratchPlateWellInput {
  rowIndex: number;
  columnIndex: number;
  role?: string | null;
  subrole?: string | null;
  group?: string | null;
  sampleSchemaId?: string | null;
  schemaTags?: Record<string, unknown> | null;
}

export interface ConcentrationWellInput {
  wellPosition: string;
  concentrationAmount?: number | null;
}

export interface ConcentrationLayerInput {
  name: string;
  concentrationUnitApiId: string;
  concentrationUnitVersionNumber: number;
  wells?: Array<ConcentrationWellInput>;
}

export interface ScratchPlateMeasurementInput {
  rowIndex: number;
  columnIndex: number;
  measurementIdentifier?: string | null;
  measurementName?: string | null;
  measurementConcept?: string | null;
  measurementValue?: number | null;
  measurementUnit?: string | null;
  detectionType?: string | null;
  analyticalMethod?: string | null;
}

export interface ScratchPlateVendorFileInput {
  scratchFileId: string;
  vendor: string;
}

export interface ScratchPlatePlateReaderAsmInput {
  scratchFileId: string;
}

export interface ContentItemInput {
  wellPosition: string;
  rowIndex: number;
  columnIndex: number;
  entityApiId: string;
  concentrationAmount: number | null;
  concentrationUnitApiId: string | null;
  concentrationUnitVersionNumber: number | null;
}

export interface MetadataWellInput {
  wellPosition: string;
  value: string | number | boolean | null;
}

export type MetadataLayerApiType = 'text' | 'integer' | 'float' | 'boolean' | 'date' | 'datetime';

export interface MetadataColumnInput {
  name: string;
  type: MetadataLayerApiType;
  wells: Array<MetadataWellInput>;
}

// -- CRUD types ------------------------------------------------------------

interface CreateScratchPlateBaseParams {
  authParentApiId: string;
  name: string;
  height: number;
  width: number;
  plateSchemaId?: string | null;
  plateMetadata?: Record<string, string | number>;
  wells?: Array<ScratchPlateWellInput>;
  concentrationLayers?: Array<ConcentrationLayerInput>;
  metadataLayers?: Array<MetadataColumnInput>;
  measurements?: Array<ScratchPlateMeasurementInput>;
  contents?: Array<Array<ContentItemInput>>;
}

export type CreateScratchPlateParams = CreateScratchPlateBaseParams &
  (
    | {vendorFile?: null; plateReaderAsm?: null}
    | {vendorFile: ScratchPlateVendorFileInput; plateReaderAsm?: null}
    | {vendorFile?: null; plateReaderAsm: ScratchPlatePlateReaderAsmInput}
  );

export interface CreateScratchPlatesResponse {
  createdIds: Array<string>;
}

export interface UpdateScratchPlateParams {
  name?: string;
  height?: number;
  width?: number;
  plateSchemaId?: string | null;
  plateMetadata?: Record<string, string | number>;
  wells?: Array<ScratchPlateWellInput>;
  concentrationLayers?: Array<ConcentrationLayerInput>;
  metadataLayers?: Array<MetadataColumnInput>;
}

export interface UpdateScratchPlatesResponse {
  updatedIds: Array<string>;
}

export interface ScratchPlateResponse {
  id: string;
  name: string;
  height: number;
  width: number;
  createdAt: string;
  modifiedAt: string;
}

export interface ListScratchPlatesParams {
  pageSize?: number;
  nextToken?: string;
}

export interface ListScratchPlatesResponse {
  items: Array<ScratchPlateResponse>;
  nextToken?: string;
}

// -- Fill types ------------------------------------------------------------

export type SampleInput =
  | {
      entityApiId: string;
      name?: null;
    }
  | {
      name: string;
      entityApiId?: null;
    };

export enum GridDirection {
  ACROSS_ROWS = 'ACROSS_ROWS',
  DOWN_COLUMNS = 'DOWN_COLUMNS',
}

export interface WellFilter {
  wellPositions?: Array<string>;
  wellRange?: string | null;
  roles?: Array<string>;
  subroles?: Array<string>;
  groups?: Array<string>;
  sampleSchemaIds?: Array<string>;
}

export interface FillGroupInput {
  samples?: Array<SampleInput>;
  filter?: WellFilter;
  numReplicates?: number;
  fillDirection?: GridDirection;
  concentration?: QuantityInput | null;
}

export interface FillScratchPlateParams {
  sourceScratchPlateId: string;
  authParentApiId: string;
  fillGroups: Array<FillGroupInput>;
}

// -- Transfer types --------------------------------------------------------

export interface WellTransferInput {
  sourcePlateId: string;
  sourceWellPosition: string;
  destinationPlateId: string;
  destinationWellPosition: string;
  transferQuantity?: QuantityInput | null;
}

export enum PlateLayoutType {
  STAMP = 'STAMP',
  STAMP_TO_QUADRANTS = 'STAMP_TO_QUADRANTS',
  REVERSE_STAMP_TO_QUADRANTS = 'REVERSE_STAMP_TO_QUADRANTS',
  INTERLEAVE = 'INTERLEAVE',
  REVERSE_INTERLEAVE = 'REVERSE_INTERLEAVE',
  COLUMN_INTERLEAVE = 'COLUMN_INTERLEAVE',
  REVERSE_COLUMN_INTERLEAVE = 'REVERSE_COLUMN_INTERLEAVE',
}

export interface TransferScratchPlateParams {
  operations?: Array<WellTransferInput>;
  sourcePlateId?: string | null;
  destinationPlateIds?: Array<string>;
  layout?: PlateLayoutType | null;
  direction?: GridDirection;
  transferQuantity?: QuantityInput | null;
  skipEmpty?: boolean;
}

// -- DAO functions ---------------------------------------------------------

export function createScratchPlates(
  creates: Array<CreateScratchPlateParams>
): Promise<CreateScratchPlatesResponse> {
  return postJSONPromise({url: API_BASE, data: {creates}});
}

export function updateScratchPlates(
  updates: Array<{id: string} & UpdateScratchPlateParams>
): Promise<UpdateScratchPlatesResponse> {
  return patchJSONPromise({url: API_BASE, data: {updates}});
}

export function bulkFillScratchPlates(
  items: Array<FillScratchPlateParams>
): Promise<TaskRunner.OngoingTaskResponse> {
  return TaskRunner.postJSONPromise({
    url: `${API_BASE}:bulk-fill`,
    data: {items},
  });
}

export function bulkTransferScratchPlates(
  items: Array<TransferScratchPlateParams>
): Promise<TaskRunner.OngoingTaskResponse> {
  return TaskRunner.postJSONPromise({
    url: `${API_BASE}:bulk-transfer`,
    data: {items},
  });
}

// -- Put in Inventory types ---------------------------------------------------

export interface PutScratchPlateInInventoryParams {
  scratchPlateId: string;
  plateSchemaId?: string | null;
  name?: string | null;
  barcodeValue?: string | null;
  sourceApiId?: string | null;
  parentStorableApiId?: string | null;
  columnMappings?: Record<string, string> | null;
  studyApiIds?: Array<string>;
}

export interface PutScratchPlateInInventoryResponse {
  plate: {id: string; name: string};
}

// -- Put in Inventory DAO function --------------------------------------------

export function putScratchPlateInInventory(
  params: PutScratchPlateInInventoryParams
): Promise<PutScratchPlateInInventoryResponse> {
  return postJSONPromise({
    url: `${API_BASE}:put-in-inventory`,
    data: {
      scratchPlateId: params.scratchPlateId,
      plateSchemaId: params.plateSchemaId ?? undefined,
      name: params.name ?? undefined,
      barcodeValue: params.barcodeValue ?? undefined,
      sourceApiId: params.sourceApiId ?? undefined,
      parentStorableApiId: params.parentStorableApiId ?? undefined,
      columnMappings: params.columnMappings ?? undefined,
      studyApiIds: params.studyApiIds ?? undefined,
    },
  });
}
